import React from 'react';
import { Avatar } from '../common/Catalyst/avatar';
import { DarkModeSwitch } from './DarkModeSwitch';

/**
 * Who is signed in: an avatar, a name and an email, with room after them
 * for the settings gear.
 *
 * `name` and `email` null means "not known yet" — the profile is still
 * loading — and the row keeps its height with nothing in it, rather than
 * saying "User" and "No email provided" for a moment and then changing.
 */
export const SidebarUserRow: React.FC<{
  initials: string;
  name: string | null;
  email: string | null;
  trailing?: React.ReactNode;
}> = ({ initials, name, email, trailing }) => (
  <div className="mt-1 flex items-center gap-1">
    <div className="flex min-w-0 flex-1 items-center gap-2.5 px-2">
      <Avatar
        initials={initials}
        className="size-7 bg-canvas-fg-strong/15 text-canvas-fg-strong"
        square
        alt=""
      />
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-medium text-canvas-fg-strong">
          {name ?? ' '}
        </span>
        <span className="block truncate text-[12px] text-canvas-fg">
          {email ?? ' '}
        </span>
      </span>
    </div>
    {trailing}
  </div>
);

/**
 * The sidebar's footer: the Dark mode switch, then who is signed in.
 *
 * Shared by the signed-in sidebar and the sign-in page's (#667), so that
 * signing in changes what the footer says and never where anything in it
 * is. With no `user` — nobody is signed in yet — the user row is still
 * laid out, just invisible: without it the switch sat 41px lower on the
 * sign-in page and jumped up as the first page opened.
 */
export const SidebarFooter: React.FC<{ user?: React.ReactNode }> = ({ user }) => (
  <div className="mx-3 mb-3 border-t border-canvas-fg-strong/10 pt-2">
    {/* Light / dark as a switch, one click from every page. */}
    <DarkModeSwitch />
    {user ?? (
      <div className="invisible" aria-hidden>
        <SidebarUserRow initials="" name={null} email={null} />
      </div>
    )}
  </div>
);
